/** @jsxRuntime automatic @jsxImportSource react */ // [C35] the PDF worker loads these files through tsx outside its own tsconfig
// Extracted from original/src/components/InstallmentsView.tsx:3309-3446 — the A4 contract and schedule print
// ("كشف أقساط وعقد بيع بالتقسيط", the `installment-print-area` element; reference
// documentation/01_visual_reference/print/p06_installment_contract-1.png). ADR-08 §2.1 "copy, then cut logic".
// Kept: the header row (initial tile, office name and line, title / number / date block), the customer box, the
// contract box with its six figures, the schedule table with its five columns and status words, the two signature
// blocks, every class string.
// Cut: the hard-coded "شركة الفيلسوف للصيرفة" / its line (R-14, F-158) → the office identity (`office`, D-052, D-125;
// the tile shows the office's first letter); `new Date(created_at).toLocaleDateString('ar-IQ')` (browser zone) →
// `dateLabel` in the office time zone (D-064, D-162); every figure → preformatted labels from the server's read model.
// Changed by decision: "المبلغ المتبقي" is the true remaining after the down payment **and the payments** (old: total −
// down payment, payments ignored, R-14 → D-109 (6)); a partial row shows its paid part (D-108); a reprint carries
// "نسخة / Copy" (D-147); a cancelled contract carries its state. All strings are `print.p06.*` keys.
import { createT } from '@faylasuf/i18n';
import type { Locale } from '@faylasuf/i18n';
import type { PrintOffice } from './StatementA4.js';

export interface InstallmentContractA4Row {
  readonly no: number;
  readonly dueDate: string;
  readonly amountLabel: string;
  readonly paidLabel: string;
  readonly status: 'unpaid' | 'partial' | 'paid';
}

export interface InstallmentContractA4Props {
  readonly locale: Locale;
  readonly id?: string;
  readonly office: PrintOffice;
  readonly contractNumber: string;
  readonly dateLabel: string;
  readonly copy: boolean;
  readonly cancelled: boolean;
  readonly customerName: string;
  readonly phone: string | null;
  readonly address: string | null;
  readonly itemName: string;
  readonly totalLabel: string;
  readonly downPaymentLabel: string;
  readonly paidLabel: string;
  readonly remainingLabel: string;
  readonly periodLabel: string;
  readonly installmentLabel: string;
  readonly rows: readonly InstallmentContractA4Row[];
}

export function InstallmentContractA4(p: InstallmentContractA4Props) {
  const t = createT(p.locale);
  const initial = (p.office.name.trim()[0] ?? 'ف').toUpperCase();
  return (
    <div id={p.id ?? 'installment-print-area'} className="font-sans p-8 bg-white text-black printable-report" style={{ direction: p.locale === 'ar' ? 'rtl' : 'ltr' }} data-testid="installment-contract-a4">
      {/* Header */}
      <div className="flex items-center justify-between border-b-2 border-gray-900 pb-4 mb-6">
        <div className="flex items-center gap-3">
          {p.office.logoUrl ? (
            <img src={p.office.logoUrl} alt="" className="w-12 h-12 rounded-xl object-contain" />
          ) : (
            <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center text-white font-black text-xl">{initial}</div>
          )}
          <div>
            <h1 className="text-xl font-black text-gray-900">{p.office.name}</h1>
            <p className="text-xs text-gray-600">{[p.office.address, p.office.phone1, p.office.phone2].filter(Boolean).join(' — ') || t('print.p06.office_line')}</p>
          </div>
        </div>
        <div className="text-left">
          <h2 className="text-base font-black text-gray-900">{t('print.p06.title')}</h2>
          <p className="text-xs text-gray-600">{t('print.p06.number', { n: p.contractNumber })}</p>
          <p className="text-xs text-gray-600">{t('print.p06.date', { d: p.dateLabel })}</p>
          {p.copy && <p className="text-xs font-black text-rose-600" data-testid="installment-contract-copy">{t('print.p06.copy')}</p>}
          {p.cancelled && <p className="text-xs font-black text-rose-600">{t('print.p06.cancelled')}</p>}
        </div>
      </div>

      {/* Section 1: Customer Info */}
      <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-xl mb-6 border border-gray-200">
        <div>
          <span className="text-[10px] text-gray-500 block font-bold">{t('print.p06.customer_name')}</span>
          <span className="text-sm font-black text-gray-900">{p.customerName}</span>
        </div>
        <div>
          <span className="text-[10px] text-gray-500 block font-bold">{t('print.p06.phone')}</span>
          <span className="text-sm font-black text-gray-900 font-mono" style={{ direction: 'ltr' }}>
            {p.phone || '-'}
          </span>
        </div>
        <div className="col-span-2">
          <span className="text-[10px] text-gray-500 block font-bold">{t('print.p06.address')}</span>
          <span className="text-sm font-black text-gray-900">{p.address || '-'}</span>
        </div>
      </div>

      {/* Section 2: Contract Info */}
      <div className="bg-gray-50 p-4 rounded-xl mb-6 border border-gray-200 space-y-3">
        <h3 className="text-xs font-black text-gray-900 border-b border-gray-200 pb-1">{t('print.p06.contract_details')}</h3>
        <div className="grid grid-cols-3 gap-4 text-xs">
          <div>
            <span className="text-[10px] text-gray-500 block font-bold">{t('print.p06.item')}</span>
            <span className="text-sm font-black text-gray-900">{p.itemName}</span>
          </div>
          <div>
            <span className="text-[10px] text-gray-500 block font-bold">{t('print.p06.total')}</span>
            <span className="text-sm font-black text-blue-600">{p.totalLabel}</span>
          </div>
          <div>
            <span className="text-[10px] text-gray-500 block font-bold">{t('print.p06.down_payment')}</span>
            <span className="text-sm font-black text-emerald-600">{p.downPaymentLabel}</span>
          </div>
          <div>
            <span className="text-[10px] text-gray-500 block font-bold">{t('print.p06.remaining')}</span>
            <span className="text-sm font-black text-amber-600" data-testid="installment-contract-remaining">{p.remainingLabel}</span>
          </div>
          <div>
            <span className="text-[10px] text-gray-500 block font-bold">{t('print.p06.period')}</span>
            <span className="text-sm font-black text-gray-900">{p.periodLabel}</span>
          </div>
          <div>
            <span className="text-[10px] text-gray-500 block font-bold">{t('print.p06.installment')}</span>
            <span className="text-sm font-black text-emerald-700">{p.installmentLabel}</span>
          </div>
          <div>
            <span className="text-[10px] text-gray-500 block font-bold">{t('print.p06.paid')}</span>
            <span className="text-sm font-black text-emerald-600">{p.paidLabel}</span>
          </div>
        </div>
      </div>

      {/* Section 3: Schedule Table */}
      <div className="mb-8">
        <h3 className="text-xs font-black text-gray-900 mb-2">{t('print.p06.schedule')}</h3>
        <table className="w-full border-collapse border border-gray-300 text-right text-xs">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-300 p-2 font-black text-gray-900 text-center">{t('print.p06.col_no')}</th>
              <th className="border border-gray-300 p-2 font-black text-gray-900">{t('print.p06.col_due')}</th>
              <th className="border border-gray-300 p-2 font-black text-gray-900 text-left">{t('print.p06.col_amount')}</th>
              <th className="border border-gray-300 p-2 font-black text-gray-900 text-left">{t('print.p06.col_paid')}</th>
              <th className="border border-gray-300 p-2 font-black text-gray-900 text-center">{t('print.p06.col_status')}</th>
            </tr>
          </thead>
          <tbody>
            {p.rows.map((r) => (
              <tr key={r.no}>
                <td className="border border-gray-300 p-2 font-bold text-center">{r.no}</td>
                <td className="border border-gray-300 p-2 font-mono font-bold">{r.dueDate}</td>
                <td className="border border-gray-300 p-2 font-mono font-bold text-left">{r.amountLabel}</td>
                <td className="border border-gray-300 p-2 font-mono font-bold text-left">{r.paidLabel}</td>
                <td className="border border-gray-300 p-2 font-bold text-center">
                  {r.status === 'paid' ? (
                    <span className="text-emerald-700 font-bold">{t('print.p06.st_paid')}</span>
                  ) : r.status === 'partial' ? (
                    <span className="text-amber-700 font-bold">{t('print.p06.st_partial')}</span>
                  ) : (
                    <span className="text-gray-600">{t('print.p06.st_due')}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer / Signatures */}
      <div className="grid grid-cols-2 gap-8 pt-12 border-t border-dashed border-gray-400 mt-12 text-center">
        <div className="space-y-16">
          <span className="text-xs text-gray-500 block font-bold">{t('print.p06.sign_office')}</span>
          <div className="w-32 h-0.5 bg-gray-400 mx-auto"></div>
        </div>
        <div className="space-y-16">
          <span className="text-xs text-gray-500 block font-bold">{t('print.p06.sign_customer')}</span>
          <div className="w-32 h-0.5 bg-gray-400 mx-auto"></div>
        </div>
      </div>
    </div>
  );
}
